// v10/src/components/ChecklistDisplay.js - WITH PENDING PULSE BADGE + ITEM CALLOUT + OBSERVATIONS + REQUIRES_PHOTO
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ChecklistItem from './ChecklistItem';
import PhotoGallery from './PhotoGallery';
import ItemCallout from './ItemCallout';

// ── Mapa de íconos por nombre de sección ─────────────────────────
const SECTION_ICONS = {
  'sección fluidos':      '🛢',  'fluidos':           '🛢',
  'combustible':          '⛽',  'aceite':            '🛢',
  'sección exterior':     '🔍',  'exterior':          '🔍',
  'estructura':           '🔩',  'fuselaje':          '✈️',
  'tren de aterrizaje':   '🛞',  'ruedas':            '🛞',
  'sección motor':        '⚙️',  'motor':             '⚙️',
  'hélice':               '🌀',  'helice':            '🌀',
  'sección cabina':       '🎛',  'cabina':            '🎛',
  'instrumentos':         '🎛',  'controles':         '🕹',
  'sección documentación':'📋',  'documentación':     '📋',
  'documentacion':        '📋',  'documentos':        '📋',
  'sección general':      '📌',  'general':           '📌',
  'preflight':            '🛫',  'prevuelo':          '🛫',
  'comunicaciones':       '📻',  'radio':             '📻',
  'emergencias':          '🚨',
};

const getSectionIcon = (sectionName) => {
  const key = sectionName?.toLowerCase().trim();
  if (SECTION_ICONS[key]) return SECTION_ICONS[key];
  for (const [k, icon] of Object.entries(SECTION_ICONS)) {
    if (key?.includes(k) || k?.includes(key)) return icon;
  }
  return '📌';
};

// ── Globo de "pendiente" que titila ──────────────────────────────
const PendingBadge = ({ text, darkMode }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.7, y: 4 }}
      style={{ position: 'absolute', top: '-11px', left: '14px', zIndex: 10, pointerEvents: 'none' }}
    >
      <motion.div
        animate={{ opacity: [1, 0.55, 1], y: [0, -2, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          padding: '3px 9px 3px 6px',
          background: darkMode
            ? 'linear-gradient(135deg, rgba(251,146,60,0.95), rgba(239,68,68,0.9))'
            : 'linear-gradient(135deg, #fb923c, #ef4444)',
          borderRadius: '20px',
          boxShadow: darkMode ? '0 3px 14px rgba(239,68,68,0.5)' : '0 3px 12px rgba(239,68,68,0.35)',
          border: '1.5px solid rgba(255,255,255,0.25)',
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, ease: 'easeOut' }}
          style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white', flexShrink: 0 }}
        />
        <span style={{ fontSize: '10px', fontWeight: 800, color: 'white', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {text || t('pending')}
        </span>
      </motion.div>
      <div style={{
        position: 'absolute', bottom: '-5px', left: '18px',
        width: 0, height: 0,
        borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
        borderTop: '5px solid #ef4444',
      }} />
    </motion.div>
  );
};

// ── Badge de observación ──────────────────────────────────────────
const ObservationBadge = ({ darkMode }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.7, y: 4 }}
      style={{ position: 'absolute', top: '-11px', right: '14px', zIndex: 10, pointerEvents: 'none' }}
    >
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        padding: '3px 9px 3px 6px',
        background: darkMode
          ? 'linear-gradient(135deg, rgba(234,179,8,0.95), rgba(245,158,11,0.9))'
          : 'linear-gradient(135deg, #eab308, #f59e0b)',
        borderRadius: '20px',
        boxShadow: darkMode ? '0 3px 14px rgba(234,179,8,0.4)' : '0 3px 12px rgba(234,179,8,0.3)',
        border: '1.5px solid rgba(255,255,255,0.25)',
      }}>
        <span style={{ fontSize: '10px', fontWeight: 800, color: 'white', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {t('observation_badge')}
        </span>
      </div>
      <div style={{
        position: 'absolute', bottom: '-5px', right: '18px',
        width: 0, height: 0,
        borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
        borderTop: '5px solid #f59e0b',
      }} />
    </motion.div>
  );
};

// ── Halo de foco para ítem pendiente ─────────────────────────────
const PendingHalo = ({ darkMode }) => (
  <motion.div
    animate={{ opacity: [0.4, 0.9, 0.4], scale: [1, 1.01, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    style={{
      position: 'absolute', inset: 0, borderRadius: '16px', zIndex: 0, pointerEvents: 'none',
      background: darkMode
        ? 'linear-gradient(135deg, rgba(251,146,60,0.07), rgba(239,68,68,0.05))'
        : 'linear-gradient(135deg, rgba(251,146,60,0.08), rgba(239,68,68,0.04))',
      boxShadow: darkMode ? 'inset 0 0 0 1.5px rgba(239,68,68,0.35)' : 'inset 0 0 0 1.5px rgba(239,68,68,0.3)',
    }}
  />
);

// ── Halo de observación ───────────────────────────────────────────
const ObservationHalo = ({ darkMode }) => (
  <motion.div
    animate={{ opacity: [0.3, 0.7, 0.3] }}
    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    style={{
      position: 'absolute', inset: 0, borderRadius: '16px', zIndex: 0, pointerEvents: 'none',
      background: darkMode
        ? 'linear-gradient(135deg, rgba(234,179,8,0.07), rgba(245,158,11,0.04))'
        : 'linear-gradient(135deg, rgba(234,179,8,0.08), rgba(245,158,11,0.04))',
      boxShadow: darkMode ? 'inset 0 0 0 1.5px rgba(234,179,8,0.3)' : 'inset 0 0 0 1.5px rgba(234,179,8,0.25)',
    }}
  />
);

// ── Botón de observación ─────────────────────────────────────────
const ObservationButton = ({ onClick, darkMode, isObserved }) => {
  const { t } = useTranslation();
  return (
    <motion.button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      title={t('report_observation')}
      style={{
        position: 'absolute', right: '8px', bottom: '8px', zIndex: 5,
        width: '28px', height: '28px', borderRadius: '9px', border: 'none',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '13px', lineHeight: 1,
        background: isObserved
          ? darkMode ? 'rgba(234,179,8,0.25)' : 'rgba(234,179,8,0.2)'
          : darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        border: isObserved
          ? `1px solid ${darkMode ? 'rgba(234,179,8,0.5)' : 'rgba(234,179,8,0.6)'}`
          : `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        transition: 'all 0.2s',
      }}
    >
      ⚠️
    </motion.button>
  );
};

const ChecklistDisplay = ({
  checklist, checkedItems, skippedItems, pendingReminders,
  currentSectionIndex, currentItemIndex, pendingItem,
  onItemInteraction, isSectionComplete, darkMode, reviewingSkipped,
  itemPhotos, onAddPhoto, onRemovePhoto, onShowReference,
  observationItems = [], onObservationRequest, onHobbsConfirm, onOilConfirm,
  onFuelConfirm, aircraft,
}) => {
  const { t } = useTranslation();
  const [calloutDismissed, setCalloutDismissed] = useState(false);

  useEffect(() => {
    setCalloutDismissed(false);
  }, [currentSectionIndex, currentItemIndex]);

  return (
    <div className="space-y-4">
      {checklist.map((section, sectionIdx) => {
        const isComplete = isSectionComplete(sectionIdx);
        const isCurrent  = sectionIdx === currentSectionIndex;
        const icon       = getSectionIcon(section.section);

        return (
          <motion.div
            key={`section-${sectionIdx}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: sectionIdx * 0.06 }}
          >
            {/* ── Section header ── */}
            <div className="flex items-center gap-2 mb-2.5 px-1">
              <motion.div
                animate={isComplete ? { scale: [1, 1.15, 1] } : {}}
                transition={{ duration: 0.4 }}
                style={{
                  width: '22px', height: '22px', borderRadius: '7px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', lineHeight: 1,
                  background: isComplete
                    ? darkMode ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.12)'
                    : isCurrent
                    ? darkMode ? 'rgba(59,130,246,0.22)' : 'rgba(59,130,246,0.12)'
                    : darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  border: isComplete
                    ? '1px solid rgba(34,197,94,0.25)'
                    : isCurrent
                    ? '1px solid rgba(59,130,246,0.25)'
                    : darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                  transition: 'all 0.25s ease',
                }}
              >
                {isComplete ? '✅' : icon}
              </motion.div>

              <h3 className={`text-xs font-bold uppercase tracking-widest ${
                isComplete  ? darkMode ? 'text-green-400' : 'text-green-600'
                : isCurrent ? darkMode ? 'text-blue-400'  : 'text-blue-600'
                :              darkMode ? 'text-gray-500'  : 'text-gray-400'
              }`}>
                {section.section}
              </h3>

              {isComplete && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}>
                  <CheckCircle2 className={`w-3.5 h-3.5 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                </motion.div>
              )}

              {(() => {
                const total = section.items.length;
                const done  = section.items.filter((_, iIdx) =>
                  checkedItems.some(c => c.section === sectionIdx && c.item === iIdx)
                ).length;
                return (
                  <span style={{
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                    color: isComplete ? (darkMode ? '#4ade80' : '#16a34a')
                         : isCurrent ? (darkMode ? '#93c5fd' : '#3b82f6')
                         : (darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8'),
                  }}>
                    {done}/{total}
                  </span>
                );
              })()}

              <div style={{ flex: 1, height: 3, borderRadius: 2, overflow: 'hidden',
                background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(section.items.filter((_, iIdx) =>
                    checkedItems.some(c => c.section === sectionIdx && c.item === iIdx)
                  ).length / Math.max(section.items.length, 1)) * 100}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  style={{
                    height: '100%', borderRadius: 2,
                    background: isComplete
                      ? 'linear-gradient(90deg,#22c55e,#16a34a)'
                      : isCurrent
                      ? 'linear-gradient(90deg,#3b82f6,#2563eb)'
                      : darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                  }}
                />
              </div>
            </div>

            {/* ── Items ── */}
            <div className="space-y-1.5">
              <AnimatePresence mode="sync">
                {section.items.map((item, itemIdx) => {
                  const fullKey           = `item-${sectionIdx}-${itemIdx}`;
                  const itemKey           = `${sectionIdx}-${itemIdx}`;
                  const isChecked         = checkedItems.some(c => c.section === sectionIdx && c.item === itemIdx);
                  const isSkipped         = skippedItems.some(s => s.section === sectionIdx && s.item === itemIdx);
                  const isPendingReminder = pendingReminders.some(p => p.section === sectionIdx && p.item === itemIdx);
                  const isCurrItem        = sectionIdx === currentSectionIndex && itemIdx === currentItemIndex;
                  const isPending         = pendingItem === item && isCurrItem;
                  const itemObj           = typeof item === 'string' ? { text: item, referenceImage: null } : item;
                  const hasRef            = !!itemObj.referenceImage;
                  const showPendingBadge  = (isSkipped || isPendingReminder) && !isChecked;

                  const observation       = observationItems.find(o => o.section === sectionIdx && o.item === itemIdx);
                  const isObserved        = !!observation;

                  const requiresPhoto     = itemObj.requires_photo === true;
                  const photos            = itemPhotos?.[itemKey] || [];
                  const hasPhoto          = photos.length > 0;

                  return (
                    <motion.div
                      key={fullKey}
                      layout="position"
                      data-tour={sectionIdx === 0 && itemIdx === 0 ? 'checklist-item' : undefined}
                      style={{
                        position: 'relative',
                        marginTop: (showPendingBadge || isObserved) ? '16px' : '0',
                        paddingBottom: onObservationRequest && !isChecked ? '4px' : '0',
                      }}
                    >
                      {isCurrItem && !isChecked && !calloutDismissed && (
                        <ItemCallout
                          item={itemObj} darkMode={darkMode}
                          onShowImage={itemObj.referenceImage ? () => onShowReference(itemObj) : null}
                          onDismiss={() => setCalloutDismissed(true)}
                        />
                      )}

                      <AnimatePresence>
                        {showPendingBadge && !isObserved && <PendingHalo key="halo-pending" darkMode={darkMode} />}
                        {isObserved && <ObservationHalo key="halo-obs" darkMode={darkMode} />}
                      </AnimatePresence>

                      <AnimatePresence>
                        {showPendingBadge && (
                          <PendingBadge key="badge-pending" text={t('missing_review')} darkMode={darkMode} />
                        )}
                        {isObserved && (
                          <ObservationBadge key="badge-obs" darkMode={darkMode} />
                        )}
                      </AnimatePresence>

                      <ChecklistItem
                        itemId={fullKey} item={item}
                        isChecked={isChecked} isSkipped={isSkipped || isPendingReminder}
                        isPending={isPending} isCurrent={isCurrItem}
                        onCheck={() => onItemInteraction(sectionIdx, itemIdx, item)}
                        index={itemIdx} reviewingSkipped={reviewingSkipped}
                        hasReferenceImage={hasRef}
                        onShowReference={hasRef ? () => onShowReference(itemObj) : null}
                        darkMode={darkMode}
                        photoGallery={
                          <PhotoGallery
                            photos={photos} onAddPhoto={onAddPhoto}
                            onRemovePhoto={onRemovePhoto} itemKey={itemKey} darkMode={darkMode}
                          />
                        }
                        requiresPhoto={requiresPhoto}
                        hasPhoto={hasPhoto}
                        onRequestPhoto={() => {}}
                        onHobbsConfirm={(value) => onHobbsConfirm?.(value)}
                        onOilConfirm={(value) => onOilConfirm?.(value)}
                        onFuelConfirm={(payload) => onFuelConfirm?.(payload)}
                        aircraft={aircraft}
                      />

                      {onObservationRequest && !isChecked && (
                        <ObservationButton
                          onClick={() => onObservationRequest(sectionIdx, itemIdx, item)}
                          darkMode={darkMode}
                          isObserved={isObserved}
                        />
                      )}

                      <AnimatePresence>
                        {isObserved && observation.comment && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{ overflow: 'hidden' }}
                          >
                            <div style={{
                              margin: '4px 8px 0', padding: '8px 12px', borderRadius: '10px',
                              background: darkMode ? 'rgba(234,179,8,0.08)' : 'rgba(234,179,8,0.07)',
                              border: `1px solid ${darkMode ? 'rgba(234,179,8,0.2)' : 'rgba(234,179,8,0.25)'}`,
                            }}>
                              <p style={{ margin: 0, fontSize: '12px', color: darkMode ? 'rgba(253,230,138,0.9)' : '#92400e', fontStyle: 'italic' }}>
                                💬 {observation.comment}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ChecklistDisplay;
